import * as React from 'react';
import * as styles from './TemplateEditPanel.css';
import { IAPanel } from '../panels/Panel';
import { IATextField } from '../textField/TextField';
import { IEnvironment } from '../../interfaces/IEnvironment';
import { IAButton } from '../button/Button';
import { ITemplate } from '../../interfaces/templates/ITemplate';
import { IAMessages } from '../messages/Index';
import { IInstance } from '../../interfaces/IInstance';
import MessageTemplateService from '../../services/MessageTemplateService';
import { LocalizationService } from '../../services/LocalizationService';
import EventTemplateService from '../../services/EventTemplateService';
import { IAEvent } from '../../../src/components/event';
import { TreeTargetingForGroups } from '../treeTargetingForGroups';

export interface ITemplateEditorProps {
  component: "messages" | "events";
  environment: IEnvironment;
  tenant: string;
  instance: IInstance;
  open: boolean;
  marginTop: number;
  isMobile: boolean;
  template: ITemplate;
  highlightColor: string;
  onClose: () => void;
  onSaveCallback: () => void;
}

export interface ITemplateEditorState {
  template: ITemplate;
  showComposer: boolean;
  isSavingTemplate: boolean;
  isLoadingGroups: boolean;
}

export class IATemplateEditPanel extends React.Component<ITemplateEditorProps, ITemplateEditorState> {
  private readonly localizationService = new LocalizationService();

  constructor(props: ITemplateEditorProps) {
    super(props);
    this.state = {
      template: undefined,
      showComposer: false,
      isSavingTemplate: false,
      isLoadingGroups: true
    };
  }

  componentDidUpdate(prevProps: Readonly<ITemplateEditorProps>): void {
    if (prevProps.open !== this.props.open) {
      this.setState({
        template: this.props.template,
        showComposer: false
      });
    }
  }

  render() {
    const hasTemplateContent = this.state.template?.jsonTemplate;
    return (
      <IAPanel
        open={this.props.open}
        transition={"slide"}
        showNavigationBar={true}
        marginTop={this.props.marginTop}
        width={this.props.isMobile ? window.innerWidth : 700}
        isMobile={this.props.isMobile}
        close={() => {
          this.setState({
            showComposer: false,
            template: undefined
          });
          this.props.onClose();
        }}
        loading={this.state.isSavingTemplate}
        navigationsBarContent={
          <div className={styles.IA_navigationBarContentWrapper}>
            <IAButton
              label={this.localizationService.strings.Messages_Categories_Save}
              buttonColor={this.props.highlightColor}
              disbaled={!this.state.template?.jsonTemplate || !this.state.template?.title}
              borderRadius={5}
              onClick={() => {
                this.setState({ isSavingTemplate: true }, () => {
                  if (this.props.component === "messages") {
                    MessageTemplateService.createOrUpdateTemplate(this.props.environment, this.props.tenant, this.props.instance.instanceName, this.state.template).then(() => {
                      this.props.onSaveCallback();
                      this.props.onClose();
                    }).catch(() => {
                      console.log("Error happened while saving template...");
                    }).finally(() => this.setState({ isSavingTemplate: false }));
                  }

                  if (this.props.component === "events") {
                    EventTemplateService.createOrUpdateTemplate(this.props.environment, this.props.tenant, this.props.instance.instanceName, this.state.template).then(() => {
                      this.props.onSaveCallback();
                      this.props.onClose();
                    }).catch(() => {
                      console.log("Error happened while saving template...");
                    }).finally(() => this.setState({ isSavingTemplate: false }));
                  }
                })
              }}
            />
            {this.state.template?.id &&
              <IAButton
                label={this.localizationService.strings.Messages_Categories_Delete}
                buttonColor={"white"}
                textColor="red"
                onClick={() => {
                  this.setState({ isSavingTemplate: true }, () => {
                    if (this.props.component === "messages") {
                      MessageTemplateService.deleteTemplate(this.props.environment, this.props.tenant, this.props.instance.instanceName, this.state.template.id).then(() => {
                        this.props.onClose();
                      }).catch(() => {
                        console.log("Error happened while deleting template...");
                      }).finally(() => this.setState({ isSavingTemplate: false }));
                    }

                    if (this.props.component === "events") {
                      EventTemplateService.deleteTemplate(this.props.environment, this.props.tenant, this.props.instance.instanceName, this.state.template.id).then(() => {
                        this.props.onClose();
                      }).catch(() => {
                        console.log("Error happened while deleting template...");
                      }).finally(() => this.setState({ isSavingTemplate: false }));
                    }
                  })
                }}
              />
            }
          </div>
        }
      >
        {this.props.open &&
          <div className={styles.IA_templateEditorWrapper}>
            <IATextField
              label={this.localizationService.strings.Messages_Template_TemplateTitle}
              borderRadius={5}
              highlightColor={this.props.highlightColor}
              onChange={text => {
                const template = this.state.template;
                template.title = text;
                this.setState({ template: template });
              }}
              text={this.state.template?.title}
            />
            <div
              style={{
                marginTop: 20
              }}
            >
              <TreeTargetingForGroups
                environment={this.props.environment}
                tenant={this.props.tenant.replace(".sharepoint.com", "")}
                application={this.props.component}
                selectedGroupIds={this.state.template?.targetingGroupIds}
                instance={this.props.template.instance}
                highlightColor={this.props.highlightColor}
                showTarget={this.state.template?.isTargeted}
                onShowTargetChanged={(isTargeted: boolean) => {
                  const template = this.state.template;
                  template.isTargeted = isTargeted;
                  this.setState({ template: template });
                }}
                onGroupIdsUpdated={(ids: number[]) => {
                  const template = this.state.template;
                  template.targetingGroupIds = ids;
                  this.setState({ template });
                }}
              />
            </div>
            <div className={styles.IA_createTemplateButtonWrapper}>
              <IAButton
                label={hasTemplateContent ? this.localizationService.strings.Messages_Template_EditTemplate : this.localizationService.strings.Messages_Template_CreateTemplate}
                buttonColor={this.props.highlightColor}
                borderRadius={5}
                icon={hasTemplateContent
                  ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Edit/SVG/ic_fluent_edit_48_regular.svg"
                  : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Add/SVG/ic_fluent_add_28_filled.svg"
                }
                onClick={() => {
                  if (this.state.showComposer) {
                    this.setState({ showComposer: false }, () => this.setState({ showComposer: true }));
                    return;
                  }
                  this.setState({ showComposer: true });
                }}
              />
            </div>

            {/* We need to include the whole Messages since it's too complicated to extract only the Composer from it :-( */}
            {this.state.showComposer &&
              <>
                {this.props.component === "messages" &&
                  <IAMessages
                    environment={this.props.environment}
                    tenant={this.props.tenant}
                    token={"000"}
                    source="SharePoint"
                    highlightColor={this.props.highlightColor}
                    isAdminPage={false}
                    settings={{
                      instance: this.props.instance,
                    }}
                    isMessagesTemplateEditor
                    initialJsonTemplate={this.state.template?.jsonTemplate}
                    saveTemplateCallback={(jsonTemplate) => {
                      const template = this.state.template;
                      template.jsonTemplate = jsonTemplate;
                      this.setState({ template, showComposer: false });
                    }}
                  />
                }
                {this.props.component === "events" &&
                  <IAEvent
                    environment={this.props.environment}
                    tenant={this.props.tenant}
                    token={"000"}
                    highlightColor={this.props.highlightColor}
                    isAdminPage={false}
                    isMessagesTemplateEditor
                    initialJsonTemplate={this.state.template?.jsonTemplate}
                    saveTemplateCallback={(jsonTemplate) => {
                      const template = this.state.template;
                      template.jsonTemplate = jsonTemplate;
                      this.setState({ template, showComposer: false });
                    }}
                    instance={this.props.instance}
                    numberOfCards={0}
                  />
                }
              </>
            }
          </div>
        }
      </IAPanel>
    );
  }
}
